import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { api, type UsuarioSessao } from "../api";

interface ContextoSessao {
  usuario: UsuarioSessao | null;
  carregando: boolean;
  entrar: (email: string, senha: string) => Promise<void>;
  verificarEmail: (token: string) => Promise<void>;
  sair: () => Promise<void>;
}

const Contexto = createContext<ContextoSessao | null>(null);

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioSessao | null>(null);
  // Começa carregando: sem isso, a tela de login pisca por um instante para quem já está logado,
  // antes de a verificação de sessão retornar.
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let cancelado = false;
    api
      .eu()
      .then(({ usuario }) => {
        if (!cancelado) setUsuario(usuario);
      })
      .catch(() => {
        // 401 aqui é o caso normal de "ninguém logado", não um erro a ser exibido.
        if (!cancelado) setUsuario(null);
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  const entrar = useCallback(async (email: string, senha: string) => {
    const { usuario } = await api.login(email, senha);
    setUsuario(usuario);
  }, []);

  // Confirmar o e-mail já loga: o endpoint cria a sessão e devolve o usuário na mesma resposta.
  const verificarEmail = useCallback(async (token: string) => {
    const { usuario } = await api.verificarEmail(token);
    setUsuario(usuario);
  }, []);

  const sair = useCallback(async () => {
    await api.logout().catch(() => undefined);
    setUsuario(null);
  }, []);

  return (
    <Contexto.Provider value={{ usuario, carregando, entrar, verificarEmail, sair }}>
      {children}
    </Contexto.Provider>
  );
}

export function useSessao(): ContextoSessao {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error("useSessao precisa estar dentro de ProvedorSessao");
  return contexto;
}
